import { useEffect, useState } from "react";

import { isRendered } from "pages/app/scenario-analysis/utils/outcomesTourTarget";

export interface UseIsOverlayOpenOptions {
  /** Watches the page only while true. */
  enabled: boolean;
  /** What counts as an overlay. */
  selector: string;
  /** Changes inside elements matching this are ignored, such as a caller's own per-frame style writes. */
  ignoreWithin?: string;
}

export const hasOpenOverlay = (selector: string) => Array.from(document.querySelectorAll(selector)).some(isRendered);

const isIgnoredMutation = (record: MutationRecord, ignoreWithin?: string) =>
  Boolean(ignoreWithin) && record.target instanceof Element && record.target.closest(ignoreWithin as string) !== null;

/** True while an element matching `selector` (a modal, drawer or dialog) is on screen. */
export const useIsOverlayOpen = ({ enabled, selector, ignoreWithin }: UseIsOverlayOpenOptions) => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    // Reset on stopping, so the next start never reads an overlay that closed while nothing was watching.
    if (!enabled) {
      setIsOpen(false);
      return undefined;
    }
    setIsOpen(hasOpenOverlay(selector));
    let frame: number | null = null;
    // Coalesced to one check a frame: the page's charts mutate the DOM constantly.
    const observer = new MutationObserver((records) => {
      if (frame !== null || records.every((record) => isIgnoredMutation(record, ignoreWithin))) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        setIsOpen(hasOpenOverlay(selector));
      });
    });
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["class", "style"] });
    return () => {
      observer.disconnect();
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [enabled, selector, ignoreWithin]);

  return enabled && isOpen;
};
